<script lang="ts">
  import { SpotSelector, ValSelect } from "$components";
  import type {
    AgreementSpot,
    Queue,
    QueueApplicantTag,
    QueueApplicantVolition,
    Tag,
  } from "$apis/parkito";
  import { Col2Grid } from "$components";
  import {
    Form,
    type FormInput,
    Validate,
  } from "../../../lib/validation_next";
  import TagSelector from "./TagSelector.svelte";
  import { derived, type Readable } from "svelte/store";
  import { log } from "$shared/utils/misc";

  export let formOutput: FormInput<QueueApplicantVolition>;
  const initialValue = $formOutput.res.optional() ?? {};

  export let context: {
    siteTags: Tag[];
    showResidentalParkingPrices: boolean;
    queue: Queue;
    agreementSpotsOfDebtor: Readable<AgreementSpot[]>;
  };

  let { agreementSpotsOfDebtor } = context;
  // Volition multiselect
  const volitionChoices = derived(
    agreementSpotsOfDebtor,
    (agreementSpots) => {
      return [
        {
          label: "Erhålla ett första avtal",
          value: "first",
          disabled: false,
        },
        { label: "Ytterliggare avtal", value: "more", disabled: false },
        {
          label: "Byta till specifik plats",
          value: "swapToSpots",
          disabled: agreementSpots.length === 0,
        },
        {
          label: "Byta till plats med egenskaper",
          value: "swapToTags",
          disabled: agreementSpots.length === 0,
        },
      ];
    },
  );

  const initialVolitionType = $volitionChoices.find((choice) => {
    return choice.value in initialValue;
  }) ?? $volitionChoices[0];

  const volitionType = Form.multiSelect(
    volitionChoices,
    initialVolitionType,
  );

  /// THIS is dumb typescript hacking that I need to do in order to make it work with our api.
  // after we move to hey-api then we probably wont need to do this.
  const initialVolitionVariant = (initialValue as unknown as any)[
    initialVolitionType.value
  ] as unknown as (QueueApplicantVolition | undefined);
  const initialTags =
    ((initialVolitionVariant as any)?.tags ?? []) as QueueApplicantTag[];
  const tagsForm = Form.input<string[]>(
    undefined,
    initialTags.map((t) => t.tagId),
  );
  const mappedTagsForm = tagsForm.mapValue((ids) =>
    ids.map((id) => ({ tagId: id }))
  );

  const initialSpots = (initialVolitionVariant as any)?.spotIds ?? [];
  log.info("initial spots", initialSpots);

  const swapToSpotsForm = Form.input<string[]>(
    Validate.minItems(1, "Minst en parkeringsplats måste väljas"),
    initialSpots,
  );

  const swapFromAgreementSpotChoices = derived(
    agreementSpotsOfDebtor,
    (spots) => {
      return spots.map((spot) => ({
        label: `${spot.name}`,
        value: spot.id,
        disable: false,
      }));
    },
  );
  const initialSwapFromSpotId = (initialVolitionVariant as any)
    ?.fromAgreementSpotId as string | undefined;
  const initialSwapFromChoice = $swapFromAgreementSpotChoices.find((spot) =>
    spot.value === initialSwapFromSpotId
  );
  const swapFromSpotIdField = Form.multiSelect(
    swapFromAgreementSpotChoices,
    initialSwapFromChoice,
  );

  const volitionFirstForm = Form.object({
    first: Form.nest(Form.object({ tags: mappedTagsForm })),
  });
  const volitionMoreForm = Form.object({
    more: Form.nest(Form.object({ tags: mappedTagsForm })),
  });
  const volitionSwapToTagsForm = Form.object({
    swapToTags: Form.nest(Form.object({
      fromAgreementSpotId: swapFromSpotIdField,
      tags: mappedTagsForm,
    })),
  });

  const volitionSwapToSpotsForm = Form.object<
    { swapToSpots: { fromAgreementSpotId: string; spotIds: string[] } }
  >({
    swapToSpots: Form.nest(Form.object({
      fromAgreementSpotId: swapFromSpotIdField,
      spotIds: swapToSpotsForm,
    })),
  });

  const volitionForm = Form.oneOf<QueueApplicantVolition>(
    volitionType,
    {
      first: Form.nest(volitionFirstForm),
      more: Form.nest(volitionMoreForm),
      swapToTags: Form.nest(volitionSwapToTagsForm),
      swapToSpots: Form.nest(volitionSwapToSpotsForm),
    },
  );

  formOutput.subscribeTo(volitionForm);

  const showTagsForm = volitionType.isOneOf(
    "first",
    "more",
    "swapToTags",
  );

  const showSpotsForm = volitionType.isOneOf("swapToSpots");
  const showSwapFromSpot = volitionType.isOneOf(
    "swapToTags",
    "swapToSpots",
  );
</script>

<h3 class="text-[15px]">Anledning till ansökan</h3>
<Col2Grid>
  <ValSelect name="volition" field={volitionType} />
</Col2Grid>
{#if $showSwapFromSpot}
  <h3 class="text-[15px]">Välj plats att byta från</h3>
  <Col2Grid>
    <ValSelect name="swapFromSpot" field={swapFromSpotIdField} />
  </Col2Grid>
{/if}
{#if $showTagsForm}
  <TagSelector tagsOutput={tagsForm} {context} />
{:else if $showSpotsForm}
  <h3 class="text-[15px]">Välj plats(er) att byta till</h3>
  <SpotSelector
    spotsOutput={swapToSpotsForm}
    context={{ ...context, queueId: context.queue.id }}
  />
{/if}
